/**
 * Voice call with a persona over WebSocket (the backend's WebSocket voice path;
 * the PiPar app uses the same endpoints).
 *
 * - /voice_chat/start (session_type voice_call, server-side tools: the server
 *   runs the AI's tools on your data, since the browser has none of its own).
 * - The microphone is recorded in the browser (AudioWorklet), resampled to
 *   16 kHz 16-bit, and a simple voice activity detector cuts it into
 *   utterances; each utterance streams to /ws/stt/stream and its text goes
 *   to the call as USER_TURN.
 * - /ws/realtime carries the AI's turn: speech as 24 kHz IMA ADPCM, as the app
 *   gets it (the Rust TTS sidecar encodes Gemini speech; 4x smaller than PCM),
 *   decoded and played as it arrives, and events (final, turn_change,
 *   tool_running, end_conversation, error).
 * - The sockets go to the session's voice engine (session_url) on the
 *   production backend, as in the app.
 * - Half duplex: while the AI thinks or speaks the microphone is ignored, so
 *   its own voice from the speakers is not taken as the user's.
 */
import { loadUserSettings, startChat, stopChat } from './api'
import type { GeneratedContent } from '../types'
import { API_BASE, getSelectedBackend } from './backendConfig'
import { PRIVATE_MODE, PRIVATE_TTS_MODEL, PRIVATE_TTS_VOICE } from './privateConfig'
import { AdpcmDecoder } from './adpcm'
import { ttsEngine } from './ttsApi'

export type CallState = 'connecting' | 'listening' | 'user_speaking' | 'recognizing' | 'thinking' | 'ai_speaking' | 'ended'

export interface CallEvents {
  onState: (state: CallState) => void
  /** The user's words while speaking (partial) and when recognized (final). */
  onUserText: (text: string, final: boolean) => void
  onAiText: (text: string) => void
  /** Tools the AI is using now (shown instead of "Thinking"). */
  onTool: (names: string[]) => void
  /** An image or document a tool made (create_image, create_document), as in the chat. */
  onGeneratedContent: (content: GeneratedContent) => void
  /** Microphone level 0..1 and whether it counts as speech. */
  onLevel: (level: number, speech: boolean) => void
  onError: (message: string) => void
  onEnded: (discussionId?: string) => void
}

export interface CallOptions {
  personaId: string
  personaName: string
  model?: string
  voice?: string
  language?: string   // voice language, e.g. "en-US"
  speed?: number
  instructions?: string
  /** The persona's TTS provider (gemini, geminiFlash, openai). */
  ttsProvider?: string
}

const STT_RATE = 16000
const TTS_RATE = 24000
// Voice activity detection
const SPEECH_START_MS = 200     // this long above the threshold starts an utterance
const SPEECH_END_MS = 900       // this long below it ends one
const PREROLL_MS = 300          // kept before the start, so the first word is not cut
const MIN_THRESHOLD = 0.012
// The room's noise level may not rise above this: speech must stay detectable
// (3x the noise) however loud the room has seemed.
const MAX_NOISE = 0.02

const STT_SERVICE = PRIVATE_MODE ? 'private' : 'gemini'

/** The WebSocket address for the call: the session's voice engine when the
 *  backend gives one. Dev and staging voice sessions stay on their own
 *  backend - the production voice engines reject them (as in the app). */
function wsBase(sessionUrl?: string): string {
  const http = sessionUrl && !import.meta.env.DEV && getSelectedBackend() === 'production'
    ? sessionUrl
    : new URL(API_BASE, window.location.href).href.replace(/\/$/, '')
  return http.replace(/^http/i, 'ws')
}

// The PiPar app's call sounds (voice_chat_screen.dart): ringing while the call
// connects, a short tone when it is the user's turn, and its default turn
// tone - a melody - when the turn goes to the persona and while a tool runs.
const SOUNDS = {
  ringing: '/sounds/ringing_new.mp3',
  yourTurn: '/sounds/turn_change_short.mp3',
  thinking: '/sounds/turn_change_with_melody.mp3',
  tool: '/sounds/turn_change_with_melody.mp3',
}

/** The call's text as the chat shows it (useChat.ts stripMessageEnvelope,
 *  MessageBubble.tsx): without the backend's [user time: ...] stamps and the
 *  VC_START: voice-call marker, which the AI may also repeat. */
function cleanCallText(text: string): string {
  let s = text.replace(/\[user time:[^\]]*\]\s*/g, '').trimStart()
  while (s.startsWith('VC_START:')) s = s.slice('VC_START:'.length).trimStart()
  return s.trim()
}

/** The app's opening prompt (pipar_realtime_service.dart makeCall): the
 *  persona answers the call with a short hello, spoken. */
const greetingPrompt = (language: string) =>
  `Hello! User is making a voice call. Respond friendly and short with user's language ${language}`

// Records the microphone in the audio thread and posts its samples.
const RECORDER_WORKLET = `
class Recorder extends AudioWorkletProcessor {
  process(inputs) {
    const ch = inputs[0] && inputs[0][0]
    if (ch) this.port.postMessage(ch.slice(0))
    return true
  }
}
registerProcessor('pipar-recorder', Recorder)
`

/** Linear resampler from the device rate to 16 kHz (keeps its phase across blocks). */
class Resampler {
  private pos = 0
  private last = 0
  constructor(private readonly ratio: number) {}
  process(input: Float32Array): Int16Array {
    const out: number[] = []
    while (this.pos < input.length) {
      const i = Math.floor(this.pos)
      const frac = this.pos - i
      const a = i === 0 ? this.last : input[i - 1]
      const b = input[i]
      const v = Math.max(-1, Math.min(1, a + (b - a) * frac))
      out.push(v < 0 ? v * 0x8000 : v * 0x7fff)
      this.pos += this.ratio
    }
    this.pos -= input.length
    this.last = input[input.length - 1] ?? this.last
    return Int16Array.from(out)
  }
}

export class VoiceCall {
  private state: CallState = 'connecting'
  private voiceChatId = ''
  /** The user's voice filler setting (Settings → AI Personas, owners), or undefined: not sent. */
  private voiceFiller: boolean | undefined
  private sessionUrl: string | undefined
  private realtime: WebSocket | null = null
  private stt: WebSocket | null = null
  private stream: MediaStream | null = null
  private micCtx: AudioContext | null = null
  private playCtx: AudioContext | null = null
  private resampler: Resampler | null = null
  private muted = false
  private ended = false
  // VAD
  private noise = 0.01
  private aboveMs = 0
  private belowMs = 0
  private preroll: Int16Array[] = []
  private prerollSamples = 0
  private pending: Int16Array[] = []
  // Playback
  private playEnd = 0
  private readonly adpcm = new AdpcmDecoder()
  private speechFormat: 'adpcm' | 'pcm' = 'adpcm'
  private pcmRemainder: Uint8Array | null = null
  private turnDone = false
  /** The tool's "one moment" is said: the next speech is the answer (the tool name goes). */
  private awaitingAnswer = false
  private ringing: HTMLAudioElement | null = null
  private toolSound: HTMLAudioElement | null = null

  constructor(private readonly options: CallOptions, private readonly events: CallEvents) {}

  get isMuted() {
    return this.muted
  }

  setMuted(muted: boolean) {
    this.muted = muted
    if (muted && this.state === 'user_speaking') this.finishUtterance()
  }

  async start(): Promise<void> {
    this.setState('connecting')
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      // Browsers give the microphone only to https pages (and localhost).
      this.fail('Voice calls need the web client over https (or on localhost): the browser allows the microphone only there.')
      return
    }
    try {
      // Microphone first: the browser asks for permission here.
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
      })
    } catch {
      this.fail('The microphone is not available. Allow it for this site and call again.')
      return
    }
    this.startRinging()
    try {
      // Read alongside the session start, so the call does not wait for it.
      const settings = loadUserSettings('web').catch(() => ({} as Record<string, unknown>))
      const session = await startChat({
        personaId: this.options.personaId,
        sessionType: 'voice_call',
        model: this.options.model,
        language: this.options.language || 'en-US', // the AI answers in it, as in the app
      })
      this.voiceChatId = session.voice_chat_id
      this.sessionUrl = session.session_url
      const filler = (await settings).voice_filler
      this.voiceFiller = typeof filler === 'boolean' ? filler : undefined
      await this.openRealtime()
      await this.startMicrophone()
      this.playCtx = new AudioContext()
      // The persona answers first, as in the app: the user's turn comes with
      // its turn_change (and the ringing stops when its voice starts).
      this.turnDone = false
      this.setState('thinking')
      this.realtime?.send(`SEND_MSG:${greetingPrompt(this.options.language || 'en-US')}`)
    } catch (e) {
      this.fail(e instanceof Error ? e.message : String(e))
    }
  }

  /** Hang up: close everything and end the session (it is saved to the chat history). */
  async hangUp(): Promise<void> {
    if (this.ended) return
    this.ended = true
    this.stopCallSounds()
    this.stt?.close()
    this.realtime?.close()
    this.stream?.getTracks().forEach((t) => t.stop())
    await this.micCtx?.close().catch(() => {})
    await this.playCtx?.close().catch(() => {})
    this.setState('ended')
    let discussionId: string | undefined
    if (this.voiceChatId) {
      try {
        discussionId = (await stopChat(this.voiceChatId, this.options.personaName)).discussion_id
      } catch {
        // the session times out on the server
      }
    }
    this.events.onEnded(discussionId)
  }

  // ---- the call socket ---------------------------------------------------------

  private openRealtime(): Promise<void> {
    const o = this.options
    // The persona's TTS provider, as the app asks for it: Gemini speech as
    // ADPCM (the Rust sidecar, or the backend, encodes it), OpenAI's as PCM.
    const engine = PRIVATE_MODE
      ? { service: 'private', model: PRIVATE_TTS_MODEL, callFormat: 'adpcm' as const }
      : ttsEngine(o.ttsProvider)
    this.speechFormat = engine.callFormat
    const params = new URLSearchParams({
      voice_chat_id: this.voiceChatId,
      language: o.language || 'en-US',
      tts_format: engine.callFormat,
      audio_format: 'pcm',
      tts_service: engine.service,
      transport: 'ws',
      sentence_mode: 'true',
      speed: String(o.speed || 1.0),
    })
    if (o.model) params.set('chat_model', o.model)
    if (engine.model) params.set('tts_model', engine.model)
    const voice = (PRIVATE_MODE && PRIVATE_TTS_VOICE) || o.voice
    if (voice) params.set('voice', voice)
    if (o.instructions) params.set('tts_instructions', o.instructions)
    // Voice fillers (a short acknowledgment while the AI thinks, progress lines
    // during slow tools): not sent leaves the backend's default, on.
    if (this.voiceFiller !== undefined) params.set('voice_filler', String(this.voiceFiller))
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(`${wsBase(this.sessionUrl)}/ws/realtime?${params}`)
      ws.binaryType = 'arraybuffer'
      this.realtime = ws
      ws.onopen = () => resolve()
      ws.onerror = () => reject(new Error('Cannot open the call to the server.'))
      ws.onclose = () => {
        if (!this.ended) this.fail('The call was disconnected.')
      }
      ws.onmessage = (ev) => {
        if (ev.data instanceof ArrayBuffer) this.playSpeech(new Uint8Array(ev.data))
        else this.onCallEvent(String(ev.data))
      }
    })
  }

  private onCallEvent(raw: string) {
    let msg: Record<string, unknown>
    try {
      msg = JSON.parse(raw)
    } catch {
      return
    }
    switch (msg.type) {
      case 'tool_running':
        this.events.onTool((msg.tools as string[]) || [])
        // As in the app: a tone while the persona works on the tool, after its "one moment".
        this.afterPlayback(() => {
          // Unless the thinking melody is still playing (one melody at a time).
          const playing = this.toolSound && !this.toolSound.paused && !this.toolSound.ended
          if (!this.ended && !this.turnDone && !playing) this.toolSound = this.playSound(SOUNDS.tool)
          // "One moment" said: the persona works on the tool (thinking, not speaking).
          if (!this.ended && !this.turnDone) {
            this.awaitingAnswer = true
            this.setState('thinking')
          }
        })
        break
      case 'generated_content':
        if (msg.data && typeof msg.data === 'object') this.events.onGeneratedContent(msg.data as GeneratedContent)
        break
      case 'final':
        if (typeof msg.ai_text === 'string') {
          const text = cleanCallText(msg.ai_text)
          if (text) this.events.onAiText(text)
        }
        break
      case 'turn_change':
        this.turnDone = true
        this.awaitingAnswer = false
        this.events.onTool([])
        this.resumeListeningWhenQuiet()
        break
      case 'end_conversation':
        this.afterPlayback(() => this.hangUp())
        break
      case 'error':
      case 'quota_exceeded':
        if (typeof msg.message === 'string') this.events.onError(msg.message)
        break
    }
  }

  // ---- AI speech -----------------------------------------------------------------

  /** 24 kHz 16-bit samples from a speech chunk: ADPCM blocks (whole ones;
   *  a block split between chunks waits for its rest), or raw PCM. */
  private decodeSpeech(chunk: Uint8Array): Int16Array | null {
    if (this.speechFormat === 'adpcm') return this.adpcm.decode(chunk)
    // Chunks may split a 16-bit sample: carry the odd byte to the next one.
    let bytes = chunk
    if (this.pcmRemainder) {
      bytes = new Uint8Array(this.pcmRemainder.length + chunk.length)
      bytes.set(this.pcmRemainder)
      bytes.set(chunk, this.pcmRemainder.length)
      this.pcmRemainder = null
    }
    if (bytes.length % 2) {
      this.pcmRemainder = bytes.slice(bytes.length - 1)
      bytes = bytes.slice(0, bytes.length - 1)
    }
    return bytes.length ? new Int16Array(bytes.slice().buffer) : null
  }

  private playSpeech(chunk: Uint8Array) {
    const ctx = this.playCtx
    if (!ctx || this.ended) return
    const samples = this.decodeSpeech(chunk)
    if (!samples) return
    const buffer = ctx.createBuffer(1, samples.length, TTS_RATE)
    const data = buffer.getChannelData(0)
    for (let i = 0; i < samples.length; i++) data[i] = samples[i] / 0x8000
    const source = ctx.createBufferSource()
    source.buffer = buffer
    source.connect(ctx.destination)
    const at = Math.max(ctx.currentTime + 0.05, this.playEnd)
    source.start(at)
    this.playEnd = at + buffer.duration
    this.stopCallSounds()
    if (this.awaitingAnswer) {
      // The answer after the tool: no longer "using" it.
      this.awaitingAnswer = false
      this.events.onTool([])
    }
    if (this.state !== 'ai_speaking') this.setState('ai_speaking')
  }

  private afterPlayback(then: () => void) {
    const ctx = this.playCtx
    const wait = ctx ? Math.max(0, this.playEnd - ctx.currentTime) : 0
    setTimeout(then, wait * 1000 + 150)
  }

  private resumeListeningWhenQuiet() {
    this.afterPlayback(() => {
      if (this.ended || !this.turnDone) return
      this.turnDone = false
      this.aboveMs = 0
      this.belowMs = 0
      this.stopCallSounds()
      this.playSound(SOUNDS.yourTurn)
      this.setState('listening')
    })
  }

  // ---- call sounds ------------------------------------------------------------------

  private playSound(src: string): HTMLAudioElement {
    const audio = new Audio(src)
    void audio.play().catch(() => {}) // a blocked or missing sound must not break the call
    return audio
  }

  /** The persona speaks (or the call ends): no ringing, no tool melody over its voice. */
  private stopCallSounds() {
    this.stopRinging()
    this.toolSound?.pause()
    this.toolSound = null
  }

  private startRinging() {
    const audio = new Audio(SOUNDS.ringing)
    audio.loop = true
    this.ringing = audio
    void audio.play().catch(() => {})
  }

  private stopRinging() {
    if (!this.ringing) return
    this.ringing.pause()
    this.ringing = null
  }

  // ---- microphone and utterances -------------------------------------------------

  private async startMicrophone() {
    const ctx = new AudioContext()
    this.micCtx = ctx
    const url = URL.createObjectURL(new Blob([RECORDER_WORKLET], { type: 'application/javascript' }))
    await ctx.audioWorklet.addModule(url)
    URL.revokeObjectURL(url)
    const node = new AudioWorkletNode(ctx, 'pipar-recorder')
    this.resampler = new Resampler(ctx.sampleRate / STT_RATE)
    node.port.onmessage = (ev) => this.onMicBlock(ev.data as Float32Array, ctx.sampleRate)
    ctx.createMediaStreamSource(this.stream!).connect(node)
  }

  private onMicBlock(block: Float32Array, rate: number) {
    if (this.ended || !this.resampler) return
    const pcm = this.resampler.process(block)
    let sum = 0
    for (let i = 0; i < block.length; i++) sum += block[i] * block[i]
    const rms = Math.sqrt(sum / block.length)
    const ms = (block.length / rate) * 1000
    const listening = this.state === 'listening' || this.state === 'user_speaking'
    const threshold = Math.max(MIN_THRESHOLD, this.noise * 3)
    const speech = !this.muted && listening && rms > threshold
    this.events.onLevel(Math.min(1, rms * 8), speech)
    if (!listening || this.muted) return

    if (this.state === 'listening') {
      // Learn the room's noise level while nobody speaks: down fast, up slowly,
      // never above MAX_NOISE. Quiet words below the threshold must not raise
      // it - that raises the threshold, until speech no longer crosses it.
      if (!speech) {
        const rate = rms < this.noise ? 0.1 : 0.005
        this.noise = Math.min(MAX_NOISE, this.noise + (rms - this.noise) * rate)
      }
      this.keepPreroll(pcm)
      this.aboveMs = speech ? this.aboveMs + ms : 0
      if (this.aboveMs >= SPEECH_START_MS) this.beginUtterance()
      return
    }
    // user_speaking: to the recognizer, or held (all of it) until its socket is open
    if (this.stt?.readyState === WebSocket.OPEN) this.stt.send(pcm.buffer)
    else this.pending.push(pcm)
    this.belowMs = speech ? 0 : this.belowMs + ms
    if (this.belowMs >= SPEECH_END_MS) this.finishUtterance()
  }

  private keepPreroll(pcm: Int16Array) {
    this.preroll.push(pcm)
    this.prerollSamples += pcm.length
    const max = (STT_RATE * (PREROLL_MS + SPEECH_START_MS)) / 1000
    while (this.prerollSamples > max && this.preroll.length > 1) {
      this.prerollSamples -= this.preroll.shift()!.length
    }
  }

  private beginUtterance() {
    this.setState('user_speaking')
    this.belowMs = 0
    const params = new URLSearchParams({
      chat_id: this.voiceChatId,
      service: STT_SERVICE,
      language: this.options.language || 'en-US',
      sample_rate: String(STT_RATE),
      audio_format: 'pcm',
    })
    const ws = new WebSocket(`${wsBase(this.sessionUrl)}/ws/stt/stream?${params}`)
    ws.binaryType = 'arraybuffer'
    this.stt = ws
    // The pre-roll (the start of the speech) and what came while opening.
    this.pending = this.preroll
    this.preroll = []
    this.prerollSamples = 0
    ws.onopen = () => {
      for (const block of this.pending) ws.send(block.buffer)
      this.pending = []
      if (this.state === 'recognizing') ws.send(`EOS:${this.voiceChatId}`)
    }
    ws.onmessage = (ev) => {
      let msg: Record<string, unknown>
      try {
        msg = JSON.parse(String(ev.data))
      } catch {
        return
      }
      if (msg.type === 'partial_transcript' && typeof msg.user_text === 'string') {
        this.events.onUserText(cleanCallText(msg.user_text), false)
      } else if (msg.type === 'transcript') {
        ws.close()
        this.onUtterance(typeof msg.user_text === 'string' ? cleanCallText(msg.user_text) : '')
      }
    }
    ws.onerror = () => {
      this.events.onError('Speech recognition is not reachable.')
      this.setState('listening')
    }
  }

  private finishUtterance() {
    if (this.state !== 'user_speaking') return
    // The user's turn ends: the app's default turn tone, a melody that plays
    // on while the persona thinks (it stops when its voice starts).
    this.toolSound?.pause()
    this.toolSound = this.playSound(SOUNDS.thinking)
    this.setState('recognizing')
    // Still opening: onopen sends what is held, then the end marker.
    if (this.stt?.readyState === WebSocket.OPEN) this.stt.send(`EOS:${this.voiceChatId}`)
  }

  private onUtterance(text: string) {
    this.stt = null
    if (this.ended) return
    if (!text) {
      this.events.onUserText('', true) // only noise: drop the partial words shown
      this.stopCallSounds() // no thinking melody: the turn stays with the user
      this.setState('listening')
      return
    }
    this.events.onUserText(text, true)
    this.turnDone = false
    this.setState('thinking')
    this.realtime?.send(`USER_TURN:${JSON.stringify({ mode: 'voice', text })}`)
  }

  // ---- state -----------------------------------------------------------------------

  private setState(state: CallState) {
    if (this.state === 'ended' && state !== 'ended') return
    this.state = state
    this.events.onState(state)
  }

  private fail(message: string) {
    this.events.onError(message)
    void this.hangUp()
  }
}
